import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tutorial } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Trash2, 
  RefreshCw,
  FileText,
  AlertCircle,
  Plus,
  Tag,
  BookOpen
} from 'lucide-react';

const DraftsPage: React.FC = () => {
  const [drafts, setDrafts] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDrafts = async () => {
    try {
      const draftsData = await apiService.getDrafts();
      setDrafts(draftsData);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      toast.error('Failed to fetch drafts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  const handleApprove = async (draftId: string) => {
    try {
      await apiService.approveDraft(draftId);
      toast.success('Draft approved successfully!');
      fetchDrafts(); // Refresh the list
    } catch (error) {
      console.error('Error approving draft:', error);
      toast.error('Failed to approve draft');
    }
  };

  const handleDelete = async (draftId: string) => {
    if (!confirm('Are you sure you want to delete this draft?')) {
      return;
    }

    try {
      await apiService.deleteDraft(draftId);
      toast.success('Draft deleted successfully!');
      fetchDrafts(); // Refresh the list
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDrafts();
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (draft: any) => {
    if (draft.title === 'Processing PDF...') {
      return 'Processing...';
    } else if (draft.title === 'Processing Failed') {
      return 'Processing Failed';
    } else if (!draft.isPublished) {
      return 'Ready for Review';
    } else {
      return 'Published';
    }
  };

  const getStatusColor = (draft: any) => {
    if (draft.title === 'Processing PDF...') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (draft.title === 'Processing Failed') {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (!draft.isPublished) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading drafts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Draft Tutorials</h1>
              <p className="mt-2 text-gray-600">
                Manage your PDF-generated tutorials that are being processed or ready for review.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/builder"
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create Tutorial</span>
              </Link>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Drafts List */}
        {drafts.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No drafts found</h3>
            <p className="text-gray-600">
              Upload a PDF to start generating tutorials. They will appear here as drafts.
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {drafts.map((draft) => (
              <div
                key={draft.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {draft.title}
                      </h3>
                      <span
                        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(draft)}`}
                      >
                        {getStatusIcon(draft)}
                        <span>{getStatusText(draft)}</span>
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{draft.description}</p>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Created: {new Date(draft.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RefreshCw className="w-4 h-4" />
                        <span>Updated: {new Date(draft.updatedAt).toLocaleDateString()}</span>
                      </div>
                      {draft.steps && (
                        <div className="flex items-center space-x-1">
                          <FileText className="w-4 h-4" />
                          <span>{draft.steps.length} steps</span>
                        </div>
                      )}
                      {draft.extractedKeywords && Array.isArray(draft.extractedKeywords) && draft.extractedKeywords.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <Tag className="w-4 h-4" />
                          <span>{draft.extractedKeywords.length} keywords</span>
                        </div>
                      )}
                    </div>

                    {/* Keywords Summary */}
                    {draft.extractedKeywords && Array.isArray(draft.extractedKeywords) && draft.extractedKeywords.length > 0 && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <BookOpen className="w-4 h-4 text-green-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <p className="text-sm font-medium text-green-800">
                                Extracted Keywords ({draft.extractedKeywords.length})
                              </p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Auto-added to Glossary
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {draft.extractedKeywords.slice(0, 8).map((keyword, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                >
                                  {keyword.term}
                                </span>
                              ))}
                              {draft.extractedKeywords.length > 8 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  +{draft.extractedKeywords.length - 8} more
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-green-600 mt-2">
                              These keywords were automatically extracted and added to the glossary during PDF processing.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {draft.status === 'error' && draft.error && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-800">Processing Error</p>
                            <p className="text-sm text-red-700 mt-1">{draft.error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {draft.title !== 'Processing PDF...' && draft.title !== 'Processing Failed' && !draft.isPublished && (
                      <>
                        <button
                          onClick={() => window.open(`/tutorials/${draft.id}`, '_blank')}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Preview</span>
                        </button>
                        <button
                          onClick={() => handleApprove(draft.id)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Approve</span>
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(draft.id)}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftsPage;
